import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import { BottomNav } from "@/components/BottomNav";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  ArrowLeft, Droplet, Home, Search, LineChart, MessageCircle,
  Plus, Clock, TrendingUp, TrendingDown, Minus, Pencil, Trash2, X
} from "lucide-react";
import { toast } from "sonner";

// Glucose unit conversion helpers - use exact /18 formula
const convertMgdlToMmol = (value) => Math.round((value / 18) * 10) / 10;
const convertMmolToMgdl = (value) => Math.round(value * 18);

// Thresholds for each unit
const THRESHOLDS = {
  "mg/dL": { low: 70, high: 180 },
  "mmol/L": { low: 3.9, high: 10.0 }
};

const GlucoseLog = () => {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [fetchingLogs, setFetchingLogs] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    glucose_value: "",
    reading_type: "random",
    notes: "",
  });

  // Edit state
  const [editingLog, setEditingLog] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    glucose_value: "",
    reading_type: "",
    notes: "",
  });

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Get user's preferred unit (default to mg/dL)
  const glucoseUnit = user?.glucose_unit || "mg/dL";
  const thresholds = THRESHOLDS[glucoseUnit];

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${API}/glucose?days=7`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      console.error("Fetch logs error:", error);
    } finally {
      setFetchingLogs(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const value = parseFloat(formData.glucose_value);
    if (isNaN(value) || value <= 0) {
      toast.error("Please enter a valid glucose value");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/glucose`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          glucose_value: value,
          reading_type: formData.reading_type,
          notes: formData.notes || null,
          input_unit: glucoseUnit, // Send the unit with the value
        }),
      });

      if (response.ok) {
        toast.success("Glucose reading logged!");
        setFormData({ glucose_value: "", reading_type: "random", notes: "" });
        setShowForm(false);
        fetchLogs();
      } else {
        toast.error("Failed to log glucose");
      }
    } catch (error) {
      console.error("Log glucose error:", error);
      toast.error("Failed to log glucose");
    } finally {
      setLoading(false);
    }
  };

  // Open edit dialog
  const handleEditClick = (log) => {
    const displayValue = getDisplayValue(log);
    setEditingLog(log);
    setEditFormData({
      glucose_value: displayValue.toString(),
      reading_type: log.reading_type,
      notes: log.notes || "",
    });
    setShowEditDialog(true);
  };

  // Handle edit submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    const value = parseFloat(editFormData.glucose_value);
    if (isNaN(value) || value <= 0) {
      toast.error("Please enter a valid glucose value");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API}/glucose/${editingLog.log_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        
        body: JSON.stringify({
          glucose_value: value,
          reading_type: editFormData.reading_type,
          notes: editFormData.notes || null,
          input_unit: glucoseUnit,
        }),
      });

      if (response.ok) {
        toast.success("Glucose reading updated!");
        setShowEditDialog(false);
        setEditingLog(null);
        fetchLogs();
      } else {
        toast.error("Failed to update glucose reading");
      }
    } catch (error) {
      console.error("Update glucose error:", error);
      toast.error("Failed to update glucose reading");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (logId) => {
    try {
      const response = await fetch(`${API}/glucose/${logId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        
      });

      if (response.ok) {
        toast.success("Glucose reading deleted!");
        setDeleteConfirm(null);
        fetchLogs();
      } else {
        toast.error("Failed to delete glucose reading");
      }
    } catch (error) {
      console.error("Delete glucose error:", error);
      toast.error("Failed to delete glucose reading");
    }
  };

  // Get display value in user's preferred unit
  const getDisplayValue = (log) => {
    // Use display_value from API if available, otherwise calculate
    if (log.display_value !== undefined) {
      return log.display_value;
    }
    // Fallback: raw value is in mg/dL, convert if needed
    if (glucoseUnit === "mmol/L") {
      return convertMgdlToMmol(log.glucose_value);
    }
    return log.glucose_value;
  };

  const getGlucoseStatus = (displayValue) => {
    if (displayValue < thresholds.low) return { label: t('glucose.low'), color: "text-destructive", bg: "bg-destructive/10", icon: TrendingDown };
    if (displayValue > thresholds.high) return { label: t('glucose.high'), color: "text-warning", bg: "bg-warning/10", icon: TrendingUp };
    return { label: t('glucose.in_range'), color: "text-success", bg: "bg-success/10", icon: Minus };
  };

  const getReadingTypeLabel = (type) => {
    const labels = {
      fasting: t('glucose.fasting'),
      before_meal: t('glucose.before_meal'),
      after_meal: t('glucose.after_meal'),
      before_bed: t('glucose.before_bed'),
      random: t('glucose.random'),
    };
    return labels[type] || type;
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Group logs by date
  const groupedLogs = logs.reduce((groups, log) => {
    const date = formatDate(log.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(log);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-orange-100 bg-background/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-muted-foreground hover:text-secondary transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="font-heading text-xl font-bold text-secondary">{t('glucose.glucose_log')}</h1>
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="rounded-full bg-primary text-white hover:bg-primary/90"
            data-testid="add-glucose-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('glucose.log')}
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Insights Quick Access */}
        <Link to="/insights" data-testid="glucose-insights-link">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100/50 p-4 flex items-center justify-between hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <LineChart className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-secondary text-sm">Trends & Insights</p>
                <p className="text-xs text-muted-foreground">View your glucose patterns and reports</p>
              </div>
            </div>
            <ArrowLeft className="w-4 h-4 text-blue-400 rotate-180" />
          </Card>
        </Link>

        {/* Add Glucose Form */}
        {showForm && (
          <Card className="bg-white rounded-3xl border border-orange-100/50 p-6 space-y-4 animate-slide-up">
            <h2 className="font-heading text-lg font-semibold text-secondary">{t('glucose.log_glucose_reading')}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="glucose_value" className="text-secondary font-medium">
                  {t('glucose.glucose_value')} ({glucoseUnit})
                </Label>
                <Input
                  id="glucose_value"
                  type="number"
                  step={glucoseUnit === "mmol/L" ? "0.1" : "1"}
                  placeholder={glucoseUnit === "mmol/L" ? "e.g., 6.7" : "e.g., 120"}
                  value={formData.glucose_value}
                  onChange={(e) => setFormData({ ...formData, glucose_value: e.target.value })}
                  className="rounded-xl border-2 border-orange-50 focus:border-primary py-5 text-2xl font-bold text-center"
                  data-testid="glucose-value-input"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="reading_type" className="text-secondary font-medium">
                  {t('glucose.reading_type')}
                </Label>
                <Select
                  value={formData.reading_type}
                  onValueChange={(value) => setFormData({ ...formData, reading_type: value })}
                >
                  <SelectTrigger className="rounded-xl border-2 border-orange-50 py-5" data-testid="reading-type-select">
                    <SelectValue placeholder={t('glucose.reading_type')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fasting">{t('glucose.fasting')}</SelectItem>
                    <SelectItem value="before_meal">{t('glucose.before_meal')}</SelectItem>
                    <SelectItem value="after_meal">{t('glucose.after_meal')}</SelectItem>
                    <SelectItem value="before_bed">{t('glucose.before_bed')}</SelectItem>
                    <SelectItem value="random">{t('glucose.random')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes" className="text-secondary font-medium">
                  {t('glucose.notes')} ({t('common.optional')})
                </Label>
                <Textarea
                  id="notes"
                  placeholder={t('glucose.any_additional_notes')}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="rounded-xl border-2 border-orange-50 focus:border-primary"
                  data-testid="glucose-notes-input"
                  rows={2}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="flex-1 rounded-full border-2 border-border py-5"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-glucose-btn"
                  className="flex-1 rounded-full bg-primary text-white hover:bg-primary/90 py-5 font-bold shadow-button-primary"
                >
                  {loading ? t('common.saving') : t('glucose.save_reading')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Readings List */}
        <Card className="bg-white rounded-3xl border border-orange-100/50 p-6 space-y-4">
          <h2 className="font-heading text-lg font-semibold text-secondary">{t('glucose.recent_readings')}</h2>

          {fetchingLogs ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-orange-50 rounded-2xl" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 space-y-4">
              <div className="w-16 h-16 rounded-full bg-orange-50 mx-auto flex items-center justify-center">
                <Droplet className="w-8 h-8 text-primary/50" />
              </div>
              <p className="text-muted-foreground">{t('glucose.no_readings')}</p>
              <p className="text-sm text-muted-foreground">
                {t('glucose.tap_log_button')}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedLogs).map(([date, dayLogs]) => (
                <div key={date} className="space-y-3">
                  <p className="text-sm font-medium text-muted-foreground">{date}</p>
                  <div className="space-y-2">
                    {dayLogs.map((log) => {
                      const displayValue = getDisplayValue(log);
                      const status = getGlucoseStatus(displayValue);
                      const StatusIcon = status.icon;
                      return (
                        <div
                          key={log.log_id}
                          className={`flex items-center justify-between p-4 rounded-2xl ${status.bg} group`}
                        >
                          <div className="flex items-center gap-3">
                            <StatusIcon className={`w-5 h-5 ${status.color}`} />
                            <div>
                              <div className="flex items-baseline gap-2">
                                <span className="text-2xl font-bold text-secondary">
                                  {displayValue}
                                </span>
                                <span className="text-sm text-muted-foreground">{glucoseUnit}</span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {getReadingTypeLabel(log.reading_type)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right mr-2">
                              <p className={`text-sm font-medium ${status.color}`}>
                                {status.label}
                              </p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatTime(log.timestamp)}
                              </p>
                            </div>
                            {/* Edit & Delete buttons */}
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditClick(log)}
                                className="h-8 w-8 rounded-full hover:bg-white/50"
                                data-testid={`edit-glucose-${log.log_id}`}
                              >
                                <Pencil className="w-4 h-4 text-muted-foreground" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteConfirm(log.log_id)}
                                className="h-8 w-8 rounded-full hover:bg-destructive/10"
                                data-testid={`delete-glucose-${log.log_id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </main>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-secondary flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              {t('glucose.edit_glucose_reading')}
            </DialogTitle>
            <DialogDescription>
              {t('glucose.update_details')}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleEditSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_glucose_value" className="text-secondary font-medium">
                {t('glucose.glucose_value')} ({glucoseUnit})
              </Label>
              <Input
                id="edit_glucose_value"
                type="number"
                step={glucoseUnit === "mmol/L" ? "0.1" : "1"}
                placeholder={glucoseUnit === "mmol/L" ? "e.g., 6.7" : "e.g., 120"}
                value={editFormData.glucose_value}
                onChange={(e) => setEditFormData({ ...editFormData, glucose_value: e.target.value })}
                className="rounded-xl border-2 border-orange-50 focus:border-primary py-5 text-2xl font-bold text-center"
                data-testid="edit-glucose-value-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_reading_type" className="text-secondary font-medium">
                {t('glucose.reading_type')}
              </Label>
              <Select
                value={editFormData.reading_type}
                onValueChange={(value) => setEditFormData({ ...editFormData, reading_type: value })}
              >
                <SelectTrigger className="rounded-xl border-2 border-orange-50 py-5" data-testid="edit-reading-type-select">
                  <SelectValue placeholder={t('glucose.reading_type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fasting">{t('glucose.fasting')}</SelectItem>
                  <SelectItem value="before_meal">{t('glucose.before_meal')}</SelectItem>
                  <SelectItem value="after_meal">{t('glucose.after_meal')}</SelectItem>
                  <SelectItem value="before_bed">{t('glucose.before_bed')}</SelectItem>
                  <SelectItem value="random">{t('glucose.random')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_notes" className="text-secondary font-medium">
                {t('glucose.notes')} ({t('common.optional')})
              </Label>
              <Textarea
                id="edit_notes"
                placeholder={t('glucose.any_additional_notes')}
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                className="rounded-xl border-2 border-orange-50 focus:border-primary"
                data-testid="edit-glucose-notes-input"
                rows={2}
              />
            </div>

            <DialogFooter className="gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="flex-1 rounded-full"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 rounded-full bg-primary text-white hover:bg-primary/90"
                data-testid="save-edit-glucose-btn"
              >
                {loading ? t('common.saving') : t('glucose.save_changes')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-secondary">
              {t('glucose.delete_reading')}
            </DialogTitle>
            <DialogDescription>
              {t('glucose.delete_reading_confirm')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
              className="flex-1 rounded-full"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm)}
              className="flex-1 rounded-full"
              data-testid="confirm-delete-glucose-btn"
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
};

export default GlucoseLog;
